/**
 * Location lookup IDs are 8-character base36 strings (0-9, A-Z)
 * Example: ABCD1234
 */
const LOCATION_LOOKUP_ID_REGEX = /^[0-9A-Z]{8}$/;

/**
 * Base36 character set for location lookup ID generation
 */
const BASE36_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/**
 * Generate a new random location lookup ID
 * @returns A new 8-character base36 lookup ID
 */
export function generateLocationLookupId(): string {
	let id = "";
	const idLength = 8;
	const randomBytes = globalThis.crypto.getRandomValues(new Uint8Array(idLength));
	for (let i = 0; i < idLength; i++) {
		const byte = randomBytes[i] ?? 0;
		id += BASE36_CHARS[byte % BASE36_CHARS.length];
	}
	return id;
}

/**
 * Validate whether a string is a valid location lookup ID
 * @param value - The string to validate
 * @returns true if the string is a valid lookup ID, false otherwise
 */
export function isValidLocationLookupId(value: string): boolean {
	return LOCATION_LOOKUP_ID_REGEX.test(value);
}

/**
 * Extract a location lookup ID from a URL path
 * Supports various URL formats:
 * - https://offstreet.io/events/ABCD1234
 * - https://offstreet.io/location/ABCD1234
 * - https://offstreet.io/park/ABCD1234
 * - https://portal-test.io/events/ABCD1234
 *
 * @param url - The URL string to extract from
 * @returns The extracted lookup ID if found and valid, undefined otherwise
 */
export function extractLocationLookupIdFromUrl(url: string): string | undefined {
	try {
		// Parse the URL
		const urlObj = new URL(url);
		const pathSegments = urlObj.pathname.split("/").filter((segment) => segment.length > 0);

		// Check each path segment to find a valid lookup ID
		for (const segment of pathSegments) {
			if (isValidLocationLookupId(segment)) {
				return segment;
			}
		}

		return undefined;
	} catch {
		// If URL parsing fails, return undefined
		return undefined;
	}
}

/**
 * Extract a location lookup ID from a string
 * Can be either a direct ID or embedded in a URL
 *
 * @param input - The input string to check
 * @returns The extracted lookup ID if found, undefined otherwise
 */
export function extractLocationLookupId(input: string): string | undefined {
	const trimmed = input.trim();

	// Check if it's a direct lookup ID first
	if (isValidLocationLookupId(trimmed)) {
		return trimmed;
	}

	// Check if it might be a URL (contains :// or starts with www.)
	if (trimmed.includes("://") || trimmed.toLowerCase().startsWith("www.")) {
		// Prepend http:// if it starts with www. but has no protocol
		const urlToCheck = trimmed.toLowerCase().startsWith("www.") ? `http://${trimmed}` : trimmed;
		return extractLocationLookupIdFromUrl(urlToCheck);
	}

	// Try to extract from what might be a partial URL path
	// e.g., "events/ABCD1234" or "/location/ABCD1234"
	const segments = trimmed.split("/").filter((s) => s.length > 0);
	for (const segment of segments) {
		if (isValidLocationLookupId(segment)) {
			return segment;
		}
	}

	return undefined;
}
